import { useCallback, useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FaixaKpis, PageHeader, SegmentedControl } from "@/components/padroes";
import { NovoEmprestimoModal } from "@/components/folha/NovoEmprestimoModal";
import { Paginacao } from "@/components/Paginacao";
import { useDebounce, usePaginado } from "@/hooks/usePaginado";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatCents, formatMesRef, parseReaisInput } from "@/lib/format";
import {
  ROTULO_BAIXA,
  ROTULO_CATEGORIA,
  ROTULO_MODALIDADE,
  type Emprestimo,
  type StatusEmprestimo,
} from "@/data/folha";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import type { User } from "@/data/mock";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, Plus, Search } from "lucide-react";

const TODOS = "__todos__";

type FiltroStatus = "ativo" | "quitado" | typeof TODOS;

const ROTULO_STATUS: Record<StatusEmprestimo, string> = {
  ativo: "Ativo",
  quitado: "Quitado",
  cancelado: "Cancelado",
};

const COR_STATUS_EMPRESTIMO: Record<StatusEmprestimo, string> = {
  ativo: "bg-success/15 text-success",
  quitado: "bg-muted text-muted-foreground",
  cancelado: "border border-border text-muted-foreground",
};

interface ResumoEmprestimos {
  saldoAberto: number;
  ativos: number;
  /** Último mês com parcela descontada em folha, e quanto foi. */
  abatidoMes: { competencia: string; valor: number; lancamentos: number } | null;
  aAbaterBonificacao: number;
  naBonificacao: number;
}

/** KPIs calculados da lista completa (sem `?page=` a rota devolve tudo). */
function calcularResumo(lista: Emprestimo[]): ResumoEmprestimos {
  const ativos = lista.filter((e) => e.status === "ativo");
  // Modalidade bonificação ainda não passada para parcelas no salário.
  const bonificacao = ativos.filter((e) => e.modalidade === "bonificacao_metas" && !e.parceladoEm);

  const porCompetencia = new Map<string, { valor: number; lancamentos: Set<string> }>();
  for (const e of lista) {
    for (const p of e.parcelas) {
      if (p.status !== "descontada") continue;
      const acc = porCompetencia.get(p.competencia) ?? { valor: 0, lancamentos: new Set<string>() };
      acc.valor += p.valor;
      acc.lancamentos.add(e.id);
      porCompetencia.set(p.competencia, acc);
    }
  }
  const ultima = [...porCompetencia.keys()].sort().pop();
  const doMes = ultima ? porCompetencia.get(ultima)! : null;

  return {
    saldoAberto: ativos.reduce((s, e) => s + e.saldo, 0),
    ativos: ativos.length,
    abatidoMes:
      ultima && doMes ? { competencia: ultima, valor: doMes.valor, lancamentos: doMes.lancamentos.size } : null,
    aAbaterBonificacao: bonificacao.reduce((s, e) => s + e.saldo, 0),
    naBonificacao: bonificacao.length,
  };
}

const RHEmprestimos = () => {
  const { user } = useAuth();
  // Lista completa só para o select do modal "Novo empréstimo" (e o vínculo na tabela).
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [status, setStatus] = useState<FiltroStatus>("ativo");
  const [busca, setBusca] = useState("");
  const buscaAtrasada = useDebounce(busca.trim());
  const [novoOpen, setNovoOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<Emprestimo | null>(null);
  const [valorBaixa, setValorBaixa] = useState("");
  const [obsBaixa, setObsBaixa] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [categoria, setCategoria] = useState(TODOS);
  const [parcelarOpen, setParcelarOpen] = useState(false);
  const [parcelasSaldo, setParcelasSaldo] = useState("3");
  const [inicioSaldo, setInicioSaldo] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  });
  const [resumo, setResumo] = useState<ResumoEmprestimos | null>(null);
  const nomeBonificacao = useNomeBonificacao();

  const ehRH = user?.role === "rh";

  const {
    items: emprestimos,
    total,
    page,
    pageSize,
    carregando,
    setPage,
    recarregar: recarregarLista,
  } = usePaginado<Emprestimo>("/emprestimos", {
    status: status === TODOS ? undefined : status,
    categoria: categoria === TODOS ? undefined : categoria,
    q: buscaAtrasada,
  });

  const carregarResumo = useCallback(async () => {
    try {
      setResumo(calcularResumo(await api.get<Emprestimo[]>("/emprestimos")));
    } catch {
      // Sem o resumo a faixa só mostra "—".
    }
  }, []);

  useEffect(() => {
    void carregarResumo();
  }, [carregarResumo]);

  // Toda alteração (novo, baixa, parcelar, adiar) mexe na lista e nos KPIs.
  const load = useCallback(async () => {
    await Promise.all([recarregarLista(), carregarResumo()]);
  }, [recarregarLista, carregarResumo]);

  useEffect(() => {
    if (!ehRH) return;
    api
      .get<User[]>("/colaboradores")
      .then(setUsuarios)
      .catch(() => toast.error("Erro ao carregar colaboradores"));
  }, [ehRH]);

  const vinculoPorId = useMemo(() => new Map(usuarios.map((u) => [u.id, u.vinculo])), [usuarios]);

  const abrirDetalhe = async (id: string) => {
    try {
      setDetalhe(await api.get<Emprestimo>(`/emprestimos/${id}`));
      setValorBaixa("");
      setObsBaixa("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar empréstimo");
    }
  };

  const registrarBaixa = async () => {
    if (!detalhe) return;
    let reais: number;
    try {
      reais = parseReaisInput(valorBaixa);
    } catch {
      toast.error("Valor inválido");
      return;
    }
    if (reais <= 0) {
      toast.error("Informe o valor da baixa");
      return;
    }

    setSalvando(true);
    try {
      await api.post(`/emprestimos/${detalhe.id}/baixas`, {
        valor: reais,
        ...(obsBaixa.trim() ? { observacao: obsBaixa.trim() } : {}),
      });
      toast.success("Baixa registrada");
      await abrirDetalhe(detalhe.id);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar baixa");
    } finally {
      setSalvando(false);
    }
  };

  const parcelarSaldo = async () => {
    if (!detalhe) return;
    const n = Number(parcelasSaldo);
    if (!Number.isInteger(n) || n < 1 || n > 120) return void toast.error("Informe de 1 a 120 parcelas");
    if (!/^\d{4}-\d{2}$/.test(inicioSaldo)) return void toast.error("Informe a competência da 1ª parcela");
    setSalvando(true);
    try {
      await api.post(`/emprestimos/${detalhe.id}/parcelar`, { numeroParcelas: n, competenciaInicial: inicioSaldo });
      toast.success("Saldo passado para parcelas no salário");
      setParcelarOpen(false);
      await abrirDetalhe(detalhe.id);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao parcelar");
    } finally {
      setSalvando(false);
    }
  };

  const postergar = async (emprestimoId: string, parcelaId: string) => {
    try {
      await api.post(`/emprestimos/${emprestimoId}/parcelas/${parcelaId}/postergar`, {});
      toast.success("Parcela adiada para a competência seguinte");
      await abrirDetalhe(emprestimoId);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao postergar parcela");
    }
  };

  const filtrando = !!buscaAtrasada || status === "quitado" || categoria !== TODOS;
  const textoVazio = filtrando
    ? "Nenhum lançamento encontrado."
    : status === "ativo"
      ? "Nenhum empréstimo ou desconto ativo."
      : "Nenhum empréstimo ou desconto registrado.";

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title="Empréstimos & Descontos">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 tabular-nums">
          <PageHeader
            titulo="Empréstimos & Descontos"
            subtitulo="Lançamentos abatidos automaticamente na folha e nas bonificações."
            acoes={
              ehRH && (
                <Button onClick={() => setNovoOpen(true)}>
                  <Plus />
                  Novo lançamento
                </Button>
              )
            }
          />

          <FaixaKpis
            minimo={180}
            itens={[
              {
                rotulo: "Saldo em aberto",
                valor: resumo ? formatCents(resumo.saldoAberto) : "—",
                apoio: resumo && `${resumo.ativos} lançamento${resumo.ativos === 1 ? "" : "s"} ativo${resumo.ativos === 1 ? "" : "s"}`,
              },
              {
                rotulo: resumo?.abatidoMes ? (
                  <>
                    Abatido em <span className="capitalize">{formatMesRef(resumo.abatidoMes.competencia).split(" ")[0]}</span>
                  </>
                ) : (
                  "Abatido no mês"
                ),
                valor: resumo ? formatCents(resumo.abatidoMes?.valor ?? 0) : "—",
                apoio: resumo && (resumo.abatidoMes ? "parcelas descontadas na folha" : "nenhuma parcela descontada ainda"),
              },
              {
                rotulo: `A abater na ${nomeBonificacao.toLowerCase()}`,
                valor: resumo ? formatCents(resumo.aAbaterBonificacao) : "—",
                apoio:
                  resumo &&
                  `${resumo.naBonificacao} lançamento${resumo.naBonificacao === 1 ? "" : "s"} na modalidade`,
              },
            ]}
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <SegmentedControl
              aria-label="Status"
              value={status}
              onChange={setStatus}
              options={[
                { value: "ativo", label: "Ativos" },
                { value: "quitado", label: "Quitados" },
                { value: TODOS, label: "Todos" },
              ]}
            />
            <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="w-44 bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos os tipos</SelectItem>
                  {Object.entries(ROTULO_CATEGORIA).map(([valor, rotulo]) => (
                    <SelectItem key={valor} value={valor}>
                      {rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="relative flex w-full max-w-xs items-center sm:w-80">
                <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar colaborador"
                  className="pl-9"
                />
              </label>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[1040px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Colaborador</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Modalidade</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Pago</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead className="w-[150px]">Parcelas</TableHead>
                  <TableHead className="pr-5">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carregando && emprestimos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : emprestimos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                      {textoVazio}
                    </TableCell>
                  </TableRow>
                ) : (
                  emprestimos.map((e) => {
                    const pagas = e.parcelas.filter((p) => p.status === "descontada").length;
                    const vinculo = vinculoPorId.get(e.colaboradorId);
                    // Na bonificação não há parcelas: o progresso é o quanto já foi pago.
                    const progresso =
                      e.parcelas.length > 0
                        ? pagas / e.parcelas.length
                        : e.valorTotal > 0
                          ? Math.min(1, e.totalPago / e.valorTotal)
                          : 0;
                    return (
                      <TableRow
                        key={e.id}
                        className="cursor-pointer"
                        onClick={() => void abrirDetalhe(e.id)}
                      >
                        <TableCell className="pl-5">
                          <div className="font-medium">{e.colaborador?.name ?? "—"}</div>
                          <div className="text-xs text-muted-foreground">
                            {[vinculo?.toUpperCase(), e.descricao].filter(Boolean).join(" · ") ||
                              e.colaborador?.email}
                          </div>
                        </TableCell>
                        <TableCell>{ROTULO_CATEGORIA[e.categoria] ?? e.categoria}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {ROTULO_MODALIDADE[e.modalidade]}
                          {e.parceladoEm && (
                            <span className="block text-xs">saldo da bonificação parcelado</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCents(e.valorTotal)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatCents(e.totalPago)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {e.saldo > 0 ? formatCents(e.saldo) : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            <span className="text-xs">
                              {e.parcelas.length > 0
                                ? `${pagas} de ${e.parcelas.length}`
                                : `${Math.round(progresso * 100)}% pago`}
                            </span>
                            <span className="flex h-1 overflow-hidden rounded-full bg-border-soft">
                              <span
                                className="rounded-full bg-primary"
                                style={{ width: `${progresso * 100}%` }}
                              />
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="pr-5">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold",
                              COR_STATUS_EMPRESTIMO[e.status],
                            )}
                          >
                            {ROTULO_STATUS[e.status] ?? e.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <Paginacao
            page={page}
            pageSize={pageSize}
            total={total}
            onPage={setPage}
            carregando={carregando}
            rotulo="lançamentos"
          />
        </div>
      </AppLayout>

      <NovoEmprestimoModal
        open={novoOpen}
        onClose={() => setNovoOpen(false)}
        usuarios={usuarios}
        onCriado={load}
      />

      {detalhe && (
        <Dialog open={!!detalhe} onOpenChange={(v) => !v && setDetalhe(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{detalhe.colaborador?.name}</DialogTitle>
              <DialogDescription>
                {ROTULO_CATEGORIA[detalhe.categoria] ?? detalhe.categoria} · {ROTULO_MODALIDADE[detalhe.modalidade]} ·{" "}
                {formatCents(detalhe.valorTotal)} · saldo
                de <strong>{formatCents(detalhe.saldo)}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[50vh] space-y-4 overflow-y-auto">
              {detalhe.parcelas.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Parcelas</Label>
                  {detalhe.parcelas.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <span>
                        {p.numero}. {formatMesRef(p.competencia)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums">{formatCents(p.valor)}</span>
                        <Badge
                          variant={p.status === "descontada" ? "secondary" : "outline"}
                          className="text-[10px]"
                        >
                          {p.status}
                        </Badge>
                        {ehRH && p.status === "pendente" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => void postergar(detalhe.id, p.id)}
                          >
                            Adiar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {detalhe.baixas && detalhe.baixas.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Baixas</Label>
                  {detalhe.baixas.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <span>
                        {ROTULO_BAIXA[b.tipo] ?? b.tipo}
                        {b.observacao && (
                          <span className="block text-xs text-muted-foreground">
                            {b.observacao}
                          </span>
                        )}
                      </span>
                      <span className="tabular-nums">{formatCents(b.valor)}</span>
                    </div>
                  ))}
                </div>
              )}

              {ehRH && detalhe.status === "ativo" && detalhe.modalidade === "bonificacao_metas" && detalhe.saldo > 0 && (
                <div className="space-y-2 rounded-md border p-3">
                  <Label className="text-xs uppercase text-muted-foreground">Passar saldo para parcelas</Label>
                  <p className="text-xs text-muted-foreground">
                    O saldo de {formatCents(detalhe.saldo)} passa a ser descontado do salário, mês a mês. A{" "}
                    {nomeBonificacao.toLowerCase()} deixa de descontar este empréstimo, mesmo que saia no meio.
                  </p>
                  {parcelarOpen ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="saldo-parcelas" className="text-xs">
                          Parcelas
                        </Label>
                        <Input
                          id="saldo-parcelas"
                          type="number"
                          min={1}
                          max={120}
                          value={parcelasSaldo}
                          onChange={(e) => setParcelasSaldo(e.target.value)}
                          className="w-24"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="saldo-inicio" className="text-xs">
                          Começa em
                        </Label>
                        <Input
                          id="saldo-inicio"
                          type="month"
                          value={inicioSaldo}
                          onChange={(e) => setInicioSaldo(e.target.value)}
                        />
                      </div>
                      <Button onClick={() => void parcelarSaldo()} disabled={salvando}>
                        {salvando && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                        Confirmar
                      </Button>
                      <Button variant="ghost" onClick={() => setParcelarOpen(false)}>
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setParcelarOpen(true)}>
                      Parcelar saldo no salário
                    </Button>
                  )}
                </div>
              )}

              {ehRH && detalhe.status === "ativo" && (
                <div className="space-y-2 rounded-md border p-3">
                  <Label className="text-xs uppercase text-muted-foreground">
                    Registrar baixa manual
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Use quando o pagamento não vier pela folha nem pela bonificação — por exemplo,
                    um pagamento feito por fora.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={valorBaixa}
                      onChange={(e) => setValorBaixa(e.target.value)}
                      placeholder="Valor (R$)"
                      inputMode="decimal"
                    />
                    <Input
                      value={obsBaixa}
                      onChange={(e) => setObsBaixa(e.target.value)}
                      placeholder="Observação"
                    />
                    <Button onClick={() => void registrarBaixa()} disabled={salvando}>
                      {salvando && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                      Baixar
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetalhe(null)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </RoleGuard>
  );
};

export default RHEmprestimos;
